import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button, InputGroup, Spinner } from '@bphxd/ds-core-react';
import { EMPTY_PERMIT } from '../../../lib/constants';
import { TPermit } from '../../../lib/types';
import { useQuery } from '@apollo/client';
import { GET_ECOW_PERMIT_BY_PERMIT_NUMBER } from '../../../graph-queries/tar/job';
import classes from './TaskPermitForm.module.scss';
import clsx from 'clsx';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import { renderOptions } from '../../../lib/util/render';
import { PERMIT_REQUIREMENT_ENUM, PERMIT_TIMING_ENUM, PERMIT_WALKED_ENUM } from '../../../lib/constants/dbConstants';
import { PermitRequirementEnum, PermitWalkedEnum } from '../../../graph-queries/__generated__/graphql';

const TaskPermitForm: FC<{
  defaultValues?: TPermit;
  taskDescription: string;
  onSubmit: (permit: TPermit) => Promise<unknown>;
  onClose: () => void;
}> = ({ defaultValues, taskDescription, onSubmit, onClose }) => {
  const notice = useNoticeBar();
  const [permitRequirement, setPermitRequirement] = useState(
    defaultValues?.permitRequirement === 'NOT_REQUIRED'
      ? PermitRequirementEnum.NotRequired
      : PermitRequirementEnum.Required
  );
  //Logic for setting the comment field to autosize height
  const [commentHeight, setCommentHeight] = useState('auto');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextAreaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      setCommentHeight(`${textarea.scrollHeight}px`);
    }
  };
  useLayoutEffect(() => {
    adjustTextAreaHeight();
  }, [defaultValues?.comments]); // Re-run if `defaultValues.comments` changes

  const handleInput = (event: { target: { style: { height: string }; scrollHeight: any } }) => {
    adjustTextAreaHeight();
  };
  const [inputTiming, setInputTiming] = useState(defaultValues?.timing || '');
  const [permitNumber, setPermitNumber] = useState(defaultValues?.permitNumber || '');
  const [inputPermitNumber, setInputPermitNumber] = useState(defaultValues?.permitNumber || '');
  const {
    data: ecowPermit,
    loading,
    error,
  } = useQuery(GET_ECOW_PERMIT_BY_PERMIT_NUMBER, {
    variables: { permitNumber },
    skip: !permitNumber,
    onError: () => notice.sendError('Unable to fetch Permit details from eCOW'),
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);

    const permit: TPermit = { ...EMPTY_PERMIT, ...Object.fromEntries(data) };
    if (defaultValues?.taskId) permit.taskId = defaultValues.taskId;
    if (taskDescription) permit.lastKnownTaskDescription = taskDescription;

    onSubmit(permit)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };

  const isValid = !!inputTiming;
  /* current user flow only requires input timing to create an entry
    !!inputTiming &&
    ((!!ecowPermit?.ecowPermit.permitNumber && !error && permitRequirement === 'Required') ||
      permitRequirement === 'Not Required');*/

  return (
    <Form className={clsx('row g-5 px-5', classes.root)} onSubmit={handleSubmit}>
      <div className="col-md-6">
        <FormGroup>
          <Label for="task-number">Task Number</Label>
          <div className={classes.details}>{defaultValues?.taskId}</div>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="task-description">Description</Label>
          <div className={classes.details}>{taskDescription}</div>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="permit-timing">Timing</Label>
          <Input
            type="select"
            name="timing"
            id="permit-timing"
            role="combobox"
            value={inputTiming}
            onChange={(e) => {
              setInputTiming(e.target.value);
            }}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(PERMIT_TIMING_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="permit-requirement">Permit Requirement</Label>
          <Input
            type="select"
            name="permitRequirement"
            id="permit-requirement"
            role="combobox"
            value={permitRequirement}
            onChange={(e) => {
              setPermitRequirement(e.target.value as PermitRequirementEnum);
            }}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(PERMIT_REQUIREMENT_ENUM)}
          </Input>
        </FormGroup>
      </div>
      {permitRequirement === 'REQUIRED' && (
        <>
          <div className="col-md-6">
            <FormGroup>
              <Label for="permit-owner">Permit Owner</Label>
              <Input
                type="text"
                name="permitOwner"
                id="permit-owner"
                defaultValue={defaultValues?.permitOwner || ''}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
        <FormGroup>
          <Label for="permit-walked">Permit Walked</Label>
          <Input
            type="select"
            name="permitWalked"
            id="permit-walked"
            role="combobox"
            className="form-select-placeholder-hack"
            defaultValue={defaultValues?.permitWalked}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(PERMIT_WALKED_ENUM)}
          </Input>
        </FormGroup>
      </div>
          <div className="col-md-12">
            <FormGroup>
              <Label for="permit-number">Permit Reference Number</Label>
              <InputGroup className="mb-3">
                <Input
                  placeholder="Search eCOW"
                  type="text"
                  name="permitNumber"
                  id="permit-number"
                  value={inputPermitNumber}
                  onChange={(e) => {
                    setInputPermitNumber((e.target as HTMLInputElement).value);
                  }}
                />
                <Button
                  disabled={!inputPermitNumber}
                  color="darker-outline"
                  onClick={() => setPermitNumber(inputPermitNumber)}
                >
                  Search
                </Button>
              </InputGroup>
            </FormGroup>
          </div>
          <div className="col-md-12">
        <FormGroup>
          <Label for="permit-comments" className="label-optional">
            Comments
          </Label>
          <Input
            type="textarea"
            innerRef={textareaRef}
            style={{ height: commentHeight }}
            onChange={handleInput}
            name="comments"
            id="permit-comments"
            defaultValue={defaultValues?.comments || ''}
          />
        </FormGroup>
      </div>
        </>
      )}
    
      {!(!!ecowPermit?.ecowPermit.permitNumber && !error) ? (
        <div className={classes.loading}>{loading && <Spinner />}</div>
      ) : (
        <>
          <div className="col-md-12">
            <FormGroup>
              <Label>Description</Label>
              <div className={classes.details}>{ecowPermit?.ecowPermit.title}</div>
            </FormGroup>
          </div>
          <div className="col-md-12">
            <FormGroup>
              <Label>Status</Label>
              <div className={classes.details}>{ecowPermit?.ecowPermit.statusComment}</div>
            </FormGroup>
          </div>
        </>
      )}

      {/* Footer */}
      <div className="col-md-12 d-flex gap-5 flex-row-reverse mt-6">
        <Button
          disabled={!isValid}
          type="submit"
          size="lg"
          className=""
          theme="darker"
          level="primary"
        >
          Save
        </Button>
        <Button size="lg" className="" theme="darker" level="tertiary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

export default TaskPermitForm;
